import React from "react";
import { useRouterState } from "@tanstack/react-router";
import { TablePaginationConfig } from "antd5";
import { isEqual } from "lodash";

import { RouterTextLink } from "components/actions/RouterLink";
import { convertBuyerFiltersToNoticeFilters } from "components/buyers/utils/convertBuyerFiltersToNoticeFilters";
import PaywallPopover from "components/paywall/PaywallPopover";
import BuyerCategoriesWithPopover from "components/table_components/BuyerCategoriesWithPopover";
import { appRoutes, SupplierContext } from "lib/appRoutes";
import { useTableRowClickthrough } from "lib/core_components/commonTableItems";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { IN_ROW_STAT_LINK, Table } from "lib/core_components/Table/Table";
import { SearchOrganisationsViaOsQuery } from "lib/generated/app-service-gql/graphql";
import { OrganisationData } from "lib/hooks/api/organisations/useOrganisations";
import { encodeCompressed } from "lib/hooks/useURLState";
import { SortState } from "lib/search/types";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { countryCodeToCountry } from "../../lib/data/optionItems";
import { SaveListDrawerProvider } from "../entities/save_components/lists/SaveListDrawerProvider";
import { BuyerColumns, getBuyersColumnCopy } from "./utils/getBuyersColumnCopy";
import { BuyerTableFilters } from "./utils/types";
import { NameColumn } from "./NameColumn";
import { TableData } from "./types";
import { DEFAULT_BUYER_FILTERS } from "./useFilterableBuyerTableState";

function getSortOrder(sort: SortState, key: string): "ascend" | "descend" | null {
  return sort.field !== key ? null : sort.order === "ASC" ? "ascend" : "descend";
}

const generateNoticeFilterSearch = (
  buyerGuid: string,
  filters: BuyerTableFilters,
): Record<string, unknown> | undefined => {
  if (!isEqual(filters, DEFAULT_BUYER_FILTERS)) {
    const noticeFilters = convertBuyerFiltersToNoticeFilters(filters, `${buyerGuid}`);
    return { noticeFilters: encodeCompressed(noticeFilters) };
  }
  return undefined;
};

/**
 * Under `/suppliers/$id/...` the buyer name + award-count cells link to the supplier↔buyer
 * relationship subpage; `undefined` elsewhere. Mirrors `getBuyerContextFromPathname` in SupplierTable.
 */
function getSupplierContextFromPathname(pathname: string): SupplierContext | undefined {
  const match = pathname.match(/^\/suppliers\/([^/]+)/);
  if (!match) return undefined;
  return { supplierId: match[1] };
}

function NoticeCountLink({
  buyer,
  countValue,
  filters,
  isSupplierRelationship,
  supplierContext,
}: {
  buyer: OrganisationData;
  countValue: number;
  filters: BuyerTableFilters;
  isSupplierRelationship?: boolean;
  supplierContext?: SupplierContext;
}) {
  if (isSupplierRelationship && supplierContext) {
    return (
      <RouterTextLink
        {...appRoutes.supplierBuyer(supplierContext, buyer.id)}
        className={IN_ROW_STAT_LINK}
        eventName={EventNames.highlightClicked}
        eventAttributes={{ "Highlight type": "Award count with supplier" }}
      >
        {countValue}
      </RouterTextLink>
    );
  }
  return (
    <RouterTextLink
      to="/buyers/$id/$"
      params={{ id: String(buyer.id), _splat: "activity" }}
      search={generateNoticeFilterSearch(buyer.id, filters)}
      className={IN_ROW_STAT_LINK}
      eventName={EventNames.highlightClicked}
      eventAttributes={{ "Highlight type": "Total record count" }}
    >
      {countValue}
    </RouterTextLink>
  );
}

function getBuyerColumns({
  sortOrder,
  selectedColumns,
  filters,
  trackingData,
  isSupplierRelationship,
  supplierContext,
}: {
  sortOrder: SortState;
  selectedColumns: BuyerColumns[];
  filters: BuyerTableFilters;
  trackingData?: EventData;
  isSupplierRelationship?: boolean;
  supplierContext?: SupplierContext;
}) {
  const name: ColumnType<OrganisationData> = {
    key: "name",
    title: "Name",
    render: (_, buyer) => (
      <NameColumn
        buyerGuid={buyer.id}
        buyerName={buyer.name}
        isSupplierRelationship={!!isSupplierRelationship}
        supplierContext={supplierContext}
        trackingData={trackingData}
        savedLists={buyer.buyerLists ?? undefined}
      />
    ),
    sorter: false, // TODO (APP-3616): Turn on again eventually
    sortOrder: getSortOrder(sortOrder, "name"),
    sizeConfig: {
      small: 360,
      medium: 360,
      large: 400,
      xlarge: 440,
    },
  };

  const countField = isSupplierRelationship ? "awardCount" : "noticeCount";
  const countValue = (buyer: OrganisationData) =>
    isSupplierRelationship ? buyer.awardCount ?? 0 : buyer.noticeCount ?? 0;

  const noticeCount: ColumnType<OrganisationData> = {
    key: countField,
    title: getBuyersColumnCopy(countField),
    render: (_, buyer) => (
      <PaywallPopover featureType="BUYERS" showModalOnClick={false} contextSource="In-row">
        <NoticeCountLink
          buyer={buyer}
          countValue={countValue(buyer)}
          filters={filters}
          isSupplierRelationship={isSupplierRelationship}
          supplierContext={supplierContext}
        />
      </PaywallPopover>
    ),
    sorter: true,
    sortOrder: getSortOrder(sortOrder, countField),
    sortDirections: ["descend", "ascend", "descend"],
    sizeConfig: {
      small: 130,
      medium: 130,
      large: 160,
      xlarge: 180,
    },
  };

  const country: ColumnType<OrganisationData> = {
    key: "country",
    title: getBuyersColumnCopy("country"),
    render: (_, buyer) => (buyer.countryCode && countryCodeToCountry(buyer.countryCode)) ?? "-",
    sorter: false, // TODO (APP-3616): Turn on again eventually
    sortOrder: getSortOrder(sortOrder, "country"),
  };

  const town: ColumnType<OrganisationData> = {
    key: "town",
    title: getBuyersColumnCopy("town"),
    render: (_, buyer) => buyer.town,
    sorter: false, // TODO (APP-3616): Turn on again eventually
    sortOrder: getSortOrder(sortOrder, "town"),
    ellipsis: true,
  };

  const buyerType: ColumnType<OrganisationData> = {
    key: "buyerType",
    title: "Type",
    render: (_, buyer) => (
      <BuyerCategoriesWithPopover
        size="default"
        buyerCategories={
          buyer.categoryAssignments
            ? buyer.categoryAssignments?.map((c) => ({
                buyer_category_id: c.categoryId,
                source: c.source,
              }))
            : []
        }
        buyerName={buyer.name}
      />
    ),
    sorter: false,
    sizeConfig: {
      small: 180,
      medium: 180,
      large: 220,
      xlarge: 260,
    },
  };

  const columns = { name, noticeCount, country, town, buyerType };
  return selectedColumns.map((column) => columns[column]);
}

export function BuyerTable({
  filters,
  sortOrder,
  onSortChange,
  selectedRows,
  onSelectedRowsChange,
  selectedColumns,
  pagination,
  onPaginationChange,
  tableData,
  isSupplierRelationship,
}: {
  filters: BuyerTableFilters;
  sortOrder: SortState;
  onSortChange: (sort: SortState) => void;
  pagination: TablePaginationConfig;
  onPaginationChange: (pagination: { current: number; pageSize: number }) => void;
  selectedRows?: string[];
  onSelectedRowsChange?: (selectedRowKeys: string[], selectedRows: OrganisationData[]) => void;
  selectedColumns: BuyerColumns[];
  tableData: TableData<SearchOrganisationsViaOsQuery | undefined>;
  /**
   * Render in supplier↔buyer relationship mode (award-count columns + links into the
   * relationship subpage). Only valid when rendered under a `/suppliers/$id` route — the
   * supplier id is read from the URL (see `supplierContext` below).
   */
  isSupplierRelationship?: boolean;
}) {
  const { trackingData } = useTracking();

  // Which supplier profile (if any) this table is nested under — derived from the URL
  // rather than passed down, so it can't drift from the actual route.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const supplierContext = React.useMemo(() => getSupplierContextFromPathname(pathname), [pathname]);

  if (process.env.NODE_ENV !== "production" && isSupplierRelationship && !supplierContext) {
    throw new Error(
      "BuyerTable: isSupplierRelationship is set but the table isn't rendered under a " +
        "/suppliers/$id route, so relationship links can't be built. Render it under a supplier " +
        "profile route, or drop isSupplierRelationship.",
    );
  }

  const buyerTrackingData: EventData = {
    ...trackingData,
    "CTA actioned": "Buyer",
  };
  const onRow = useTableRowClickthrough<OrganisationData>((r) =>
    isSupplierRelationship && supplierContext
      ? appRoutes.supplierBuyer(supplierContext, r.id)
      : appRoutes.buyerDetail(r.id),
  );

  const columns = getBuyerColumns({
    sortOrder,
    selectedColumns,
    filters,
    trackingData: buyerTrackingData,
    isSupplierRelationship,
    supplierContext,
  });

  const {
    queryResult: { data, isLoading, isError },
  } = tableData;

  const organisationsData: OrganisationData[] = data?.searchOrganisations?.orgs ?? [];
  const total = data?.searchOrganisations?.total;

  return (
    <SaveListDrawerProvider>
      <Table<OrganisationData>
        ariaLabel="Buyer table"
        dataSource={organisationsData}
        columns={columns}
        loading={isLoading}
        isError={isError}
        pagination={{
          ...pagination,
          total,
          onChange: (current, pageSize) => onPaginationChange({ current, pageSize }),
          showSizeChanger: true,
        }}
        onChange={(_, __, sorter) => {
          if (Array.isArray(sorter)) {
            sorter = sorter[0];
          }
          // If the sort order is not defined, we don't want to trigger a sort without an order
          if (!sorter.order) {
            return;
          }
          const newSortState: SortState = {
            order: sorter.order === "descend" ? "DESC" : "ASC",
            field: sorter.columnKey as string,
          };
          if (!isEqual(newSortState, sortOrder)) {
            onSortChange(newSortState);
          }
        }}
        rowSelection={
          onSelectedRowsChange &&
          selectedRows && {
            onChange: (selectedRowKeys, selectedRows) => {
              onSelectedRowsChange(selectedRowKeys as string[], selectedRows);
            },
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRows,
          }
        }
        rowKey="id"
        onRow={onRow}
      />
    </SaveListDrawerProvider>
  );
}
